// src/pages/Commerce/GeneralCommercePartnersReview.tsx

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  listenToCommerceData,
  upsertCommercePartner,
} from "../../../../redux/commercePartnersSlice";
import { AppDispatch, RootState } from "../../../../redux/store";

const GeneralCommercePartnersReview: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const partner = useSelector(
    (state: RootState) => state.commercePartners.currentPartner
  );

  const [error, setError] = useState<string | null>(null);

  /* ✅ EXACT same pattern as ShortReview */
  useEffect(() => {
    if (!brandId) return;

    const unsubscribe = dispatch(listenToCommerceData(brandId));

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [dispatch, brandId]);

  const handleApproval = async (update: Partial<any>) => {
    if (!brandId) return;

    try {
      await dispatch(
        upsertCommercePartner({
          brandId,
          data: {
            // baseline reset
            isPublished: false,
            isFlagged: false,
            isComplete: false,
            inReview: false,
            isDraft: false,
            ...update,
          },
        })
      ).unwrap();

      alert("✅ Partner status updated");
    } catch (err) {
      console.error(err);
      setError("Failed to update partner.");
    }
  };

  if (!partner) return <div style={styles.loading}>Loading partner...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.reviewCard}>
        <img
          src={partner.image || "/default-image.jpg"}
          alt={partner.name || "Partner"}
          style={styles.image}
        />

        <div style={styles.content}>
          <h2 style={styles.title}>
            {partner.name || "Untitled Partner"}
          </h2>

          <span style={styles.status}>
            {partner.isPublished
              ? "Published"
              : partner.isFlagged
              ? "Flagged"
              : partner.inReview
              ? "In Review"
              : "Pending Review"}
          </span>

          <p style={styles.meta}>
            <strong>Created:</strong>{" "}
            {new Date(partner.joinDate).toLocaleString()}
          </p>

          <div style={styles.actions}>
            {/* ✅ Approve */}
            <button
              style={{
                ...styles.button,
                background: "linear-gradient(to right, #00c853, #64dd17)",
              }}
              onClick={() =>
                handleApproval({
                  isPublished: true,
                  isFlagged: false,
                  isComplete: true,
                  inReview: false,
                })
              }
            >
              Approve
            </button>

            {/* ❌ Reject */}
            <button
              style={{
                ...styles.button,
                background: "linear-gradient(to right, #ff9100, #ff5722)",
              }}
              onClick={() =>
                handleApproval({
                  isPublished: false,
                  isFlagged: false,
                  isComplete: false,
                  inReview: false,
                })
              }
            >
              Reject
            </button>

            {/* 🚩 Disable */}
            <button
              style={{
                ...styles.button,
                background: "linear-gradient(to right, #d32f2f, #b71c1c)",
              }}
              onClick={() =>
                handleApproval({
                  isPublished: false,
                  isFlagged: true,
                  isComplete: false,
                  inReview: true,
                })
              }
            >
              Disable
            </button>

            {/* 👀 Status Display */}
            <button style={styles.disabledButton} disabled>
              {partner.isPublished
                ? "Published"
                : partner.isFlagged
                ? "Flagged"
                : partner.inReview
                ? "In Review"
                : "Pending"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralCommercePartnersReview;


// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    background:
      "linear-gradient(90deg, #0A66C2 0%, #000000ff 60%)",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
  },
  loading: {
    color: "#fff",
    textAlign: "center",
    padding: "40px",
  },
  error: {
    color: "red",
    fontWeight: "bold",
    padding: "40px",
  },
  reviewCard: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#111",
    borderRadius: "16px",
    padding: 24,
    maxWidth: 600,
    width: "100%",
    boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.05)",
  },
  image: {
    width: "100%",
    height: "240px",
    objectFit: "cover",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    color: "#fff",
  },
  title: {
    fontSize: "22px",
    fontWeight: 700,
  },
  status: {
    backgroundColor: "#222",
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "13px",
    width: "fit-content",
    color: "#0A66C2",
  },
  meta: {
    fontSize: "14px",
    color: "#bbb",
  },
  actions: {
    marginTop: "20px",
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  button: {
    flex: "1 1 45%",
    padding: "10px 15px",
    borderRadius: "8px",
    border: "none",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  disabledButton: {
    backgroundColor: "#333",
    color: "#ccc",
    padding: "10px 15px",
    borderRadius: "8px",
    flex: "1 1 45%",
    border: "none",
  },
};